/**
 * BracketTree — SVG-based single-elimination tournament bracket.
 *
 * Layout:
 *   - Column 0 = round 1 (first round of current tournament)
 *   - Column R-1 = final
 *   - Each column shows matches; match positions are spaced so they converge
 *     toward the right.
 *
 * Only the CURRENT round has player names; all later rounds show "?" placeholders.
 * SVG connector lines link each pair of matches in round r to their match in round r+1.
 */
import type { RPSMatchState } from '@/store/rpsStore';
import styles from './BracketTree.module.css';

// ─── Layout constants ─────────────────────────────────────────────────────────
const MATCH_W = 180;    // card width (px)
const MATCH_H = 64;     // card height (px) — two player slots
const SLOT_H = 30;      // height of a single player slot inside the card
const COL_GAP = 64;     // horizontal gap between columns
const UNIT = MATCH_H + 16; // vertical spacing unit = card height + padding

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Top position (px) of match `i` (0-indexed) in round `r` (1-indexed),
 * given total rounds T.
 *
 * Formula: top = (2^(r-1) - 1) * UNIT/2 + i * 2^(r-1) * UNIT
 */
function matchTop(r: number, i: number): number {
  const factor = Math.pow(2, r - 1);
  return (factor - 1) * (UNIT / 2) + i * factor * UNIT;
}

/** Number of matches in round r given total initial participants (always power-of-2 after byes). */
function matchCount(r: number, totalRounds: number): number {
  return Math.pow(2, totalRounds - r);
}

/** Round labels from right to left (final is last). */
function getRoundLabels(totalRounds: number): string[] {
  const labels: string[] = [];
  for (let r = 1; r <= totalRounds; r++) {
    if (r === totalRounds) labels.push('Chung kết');
    else if (r === totalRounds - 1) labels.push('Bán kết');
    else if (r === totalRounds - 2) labels.push('Tứ kết');
    else if (r === totalRounds - 3) labels.push('Vòng 1/8');
    else labels.push(`Vòng ${r}`);
  }
  return labels;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type PlayerSlotProps = {
  name: string | null;
  isWinner: boolean;
  isMe: boolean;
};

function PlayerSlot({ name, isWinner, isMe }: PlayerSlotProps) {
  return (
    <div
      className={[
        styles.slot,
        isWinner ? styles.slotWinner : '',
        isMe ? styles.slotMe : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ height: SLOT_H }}
    >
      <span className={styles.slotName}>{name ?? '?'}</span>
      {isWinner && <span className={styles.slotWinBadge}>✓</span>}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export type BracketTreeProps = {
  /** All matches of the current tournament round (not all matches ever played). */
  currentMatches: RPSMatchState[];
  /** Round number being shown (1-indexed; server's tournament_round). */
  currentRound: number;
  /** Total rounds in this tournament (ceil(log2(initialParticipantCount))). */
  totalRounds: number;
  /** participant_id of the logged-in user (to highlight). */
  myParticipantId: string | null;
  /** Completed matches from past rounds, keyed by tournament_round number. */
  bracketHistory?: Record<number, RPSMatchState[]>;
};

export function BracketTree({
  currentMatches,
  currentRound,
  totalRounds,
  myParticipantId,
  bracketHistory = {},
}: BracketTreeProps) {
  if (totalRounds < 1) return null;

  // Map column index to round number.
  // The current server round maps to column (currentRound - 1).
  // We always render all columns 1..totalRounds.

  // Total SVG height: determined by column 1 (most matches).
  const col1Count = matchCount(1, totalRounds);
  const svgH = col1Count * UNIT;
  const svgW = totalRounds * (MATCH_W + COL_GAP) + COL_GAP;

  // Pre-compute current round match by index for quick lookup.
  // We assume matches arrive in bracket order (match 0, 1, 2 ...).
  const currentMatchByIndex = currentMatches;

  // For winner slot lookup (when a match is completed).
  const winnerName = (m: RPSMatchState): string | null => {
    if (!m.winner_id) return null;
    if (m.player1.participant_id === m.winner_id) return m.player1.display_name;
    if (m.player2 && m.player2.participant_id === m.winner_id) return m.player2.display_name;
    return null;
  };

  const roundLabels = getRoundLabels(totalRounds);

  return (
    <div className={styles.root}>
      <div className={styles.scroll}>
        {/* Column headers */}
        <div
          className={styles.headers}
          style={{ width: svgW, height: 28 }}
        >
          {Array.from({ length: totalRounds }, (_, ci) => {
            const r = ci + 1;
            const colX = ci * (MATCH_W + COL_GAP) + COL_GAP / 2;
            return (
              <div
                key={r}
                className={[
                  styles.colHeader,
                  r === currentRound ? styles.colHeaderActive : '',
                ].join(' ')}
                style={{ left: colX, width: MATCH_W }}
              >
                {roundLabels[ci]}
              </div>
            );
          })}
        </div>

        {/* SVG layer for connector lines */}
        <svg
          className={styles.svg}
          viewBox={`0 0 ${svgW} ${svgH}`}
          width={svgW}
          height={svgH}
          xmlns="http://www.w3.org/2000/svg"
        >
          {Array.from({ length: totalRounds - 1 }, (_, ci) => {
            const r = ci + 1; // source round
            const count = matchCount(r, totalRounds);
            const x1 = ci * (MATCH_W + COL_GAP) + COL_GAP / 2 + MATCH_W; // right edge of source card
            const x2 = (ci + 1) * (MATCH_W + COL_GAP) + COL_GAP / 2;      // left edge of target card
            const xMid = (x1 + x2) / 2;

            return Array.from({ length: count / 2 }, (_, pi) => {
              // pair pi (0-indexed): top match = 2*pi, bottom match = 2*pi+1
              const topMatch = 2 * pi;
              const botMatch = 2 * pi + 1;
              const y1 = matchTop(r, topMatch) + MATCH_H / 2;
              const y2 = matchTop(r, botMatch) + MATCH_H / 2;
              const yMid = (y1 + y2) / 2;
              const yTarget = matchTop(r + 1, pi) + MATCH_H / 2;

              return (
                <g key={`conn-r${r}-p${pi}`} className={styles.connectorGroup}>
                  {/* Top arm: horizontal from top match */}
                  <line x1={x1} y1={y1} x2={xMid} y2={y1} />
                  {/* Bottom arm: horizontal from bottom match */}
                  <line x1={x1} y1={y2} x2={xMid} y2={y2} />
                  {/* Vertical bar */}
                  <line x1={xMid} y1={y1} x2={xMid} y2={y2} />
                  {/* Output arm to target match */}
                  <line x1={xMid} y1={yMid} x2={x2} y2={yTarget} />
                </g>
              );
            });
          })}
        </svg>

        {/* Match cards layer */}
        <div
          className={styles.cards}
          style={{ width: svgW, height: svgH }}
        >
          {Array.from({ length: totalRounds }, (_, ci) => {
            const r = ci + 1;
            const count = matchCount(r, totalRounds);
            const colX = ci * (MATCH_W + COL_GAP) + COL_GAP / 2;
            const isCurrentRound = r === currentRound;
            const isPastRound = r < currentRound;
            const isFuture = r > currentRound;
            // Matches for past rounds come from bracketHistory
            const pastMatches = isPastRound ? (bracketHistory[r] ?? []) : [];

            return Array.from({ length: count }, (_, i) => {
              const top = matchTop(r, i);
              const match: RPSMatchState | null =
                isCurrentRound ? (currentMatchByIndex[i] ?? null)
                : isPastRound ? (pastMatches[i] ?? null)
                : null;

              // Determine names for each slot
              let p1Name: string | null = null;
              let p2Name: string | null = null;
              let p1IsWinner = false;
              let p2IsWinner = false;
              let p1IsMe = false;
              let p2IsMe = false;
              let isBye = false;

              if (match) {
                p1Name = match.player1.display_name;
                p2Name = match.player2?.display_name ?? null;
                isBye = match.is_bye;
                p1IsWinner = match.winner_id === match.player1.participant_id;
                p2IsWinner = !!match.player2 && match.winner_id === match.player2.participant_id;
                p1IsMe = match.player1.participant_id === myParticipantId;
                p2IsMe = !!match.player2 && match.player2.participant_id === myParticipantId;
              }

              const isMyMatch = match ? p1IsMe || p2IsMe : false;

              return (
                <div
                  key={`r${r}-m${i}`}
                  className={[
                    styles.matchCard,
                    isCurrentRound ? styles.matchCardCurrent : '',
                    isPastRound ? styles.matchCardPast : '',
                    isFuture ? styles.matchCardFuture : '',
                    isMyMatch ? styles.matchCardMe : '',
                    isBye ? styles.matchCardBye : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    position: 'absolute',
                    left: colX,
                    top,
                    width: MATCH_W,
                    height: MATCH_H,
                  }}
                >
                  {isFuture || !match ? (
                    <>
                      <div className={styles.slotPlaceholder} style={{ height: SLOT_H }}>?</div>
                      <div className={styles.slotDivider} />
                      <div className={styles.slotPlaceholder} style={{ height: SLOT_H }}>?</div>
                    </>
                  ) : (
                    <>
                      <PlayerSlot name={p1Name} isWinner={p1IsWinner} isMe={p1IsMe} />
                      <div className={styles.slotDivider} />
                      {isBye ? (
                        <div className={styles.slotBye} style={{ height: SLOT_H }}>BYE</div>
                      ) : (
                        <PlayerSlot name={p2Name} isWinner={p2IsWinner} isMe={p2IsMe} />
                      )}
                    </>
                  )}
                </div>
              );
            });
          })}
        </div>
      </div>
    </div>
  );
}
